import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import countries from "i18n-iso-countries";
import { MdArrowBack, MdChevronRight } from "react-icons/md";

import CurrencyAmountInput from "./CurrencyAmountInput";
import ChooseAgentOption from "../businessUtilities/ChooseAgentOption";
import { defaultCurrencyForCountry } from "../../common/commonUtils/currencyUtils";
import { AgentBalancesApi as AgentBalanceApi } from "../../../api/common/apiIndex";

try { countries.registerLocale(require("i18n-iso-countries/langs/en.json")); } catch {}

const normalizeCountryCode = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (/^[a-z]{2}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  try {
    const iso = countries.getAlpha2Code(trimmed, "en");
    return iso ? iso.toUpperCase() : "";
  } catch {
    return "";
  }
};

const toCountryName = (value) => {
  const code = normalizeCountryCode(value);
  if (!code) return value || "";
  try {
    return countries.getName(code, "en") || code;
  } catch {
    return code;
  }
};

const fmtMoney = (n, currency = "USD") => {
  const amount = Number.isFinite(Number(n)) ? Number(n) : 0;
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return (
      new Intl.NumberFormat("sv-SE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount) + (currency ? ` ${currency}` : "")
    );
  }
};

const getErrorText = (err) => {
  if (!err) return "Unknown error";
  const d = err?.response?.data;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") {
    if (d.message) return d.message;
    if (d.error) return d.error;
    const statusBits = [d.status, d.code, d.path].filter(Boolean).join(" · ");
    return statusBits || JSON.stringify(d);
  }
  return err.message || "Unknown error";
};

const TransferDetailsForm = ({
  senderData,
  recipientData,
  agents,
  onBack,
  onSubmit,
  backToUrl,
  prefillTransfer = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState(() => ({
    amount: prefillTransfer?.amount != null ? String(prefillTransfer.amount) : "",
    receivedAmount: prefillTransfer?.receivedAmount != null ? String(prefillTransfer.receivedAmount) : "",
    agentId: "",
  }));

  const [senderCurrency, setSenderCurrency] = useState("USD");
  const [recipientCurrency, setRecipientCurrency] = useState("USD");
  const [showErrors, setShowErrors] = useState(false);
  const prefillAppliedRef = useRef(false);

  useEffect(() => {
    if (senderData?.country) {
      setSenderCurrency(defaultCurrencyForCountry(senderData.country) || "USD");
    }
  }, [senderData?.country]);

  useEffect(() => {
    if (recipientData?.country) {
      setRecipientCurrency(defaultCurrencyForCountry(recipientData.country) || "USD");
    }
  }, [recipientData?.country]);

  useEffect(() => {
    if (!prefillTransfer || prefillAppliedRef.current) return;
    setFormData((prev) => ({
      ...prev,
      amount: prefillTransfer.amount != null ? String(prefillTransfer.amount) : prev.amount,
      receivedAmount:
        prefillTransfer.receivedAmount != null ? String(prefillTransfer.receivedAmount) : prev.receivedAmount,
    }));
    if (prefillTransfer.sendCurrency) {
      setSenderCurrency(prefillTransfer.sendCurrency);
    }
    if (prefillTransfer.receiveCurrency) {
      setRecipientCurrency(prefillTransfer.receiveCurrency);
    }
    prefillAppliedRef.current = true;
  }, [prefillTransfer]);

  const agentId = formData.agentId ? Number(formData.agentId) : null;
  const payoutCountry = normalizeCountryCode(recipientData?.country) || null;
  const payoutCurrency = recipientCurrency;

  const snapshotReady = Boolean(agentId && payoutCountry && payoutCurrency);

  const country2 = useMemo(() => {
    return payoutCountry || "";
  }, [payoutCountry]);

  const payoutCountryLabel = useMemo(() => toCountryName(payoutCountry), [payoutCountry]);

  const snapshotQuery = useQuery({
    queryKey: ["agent-snapshot", agentId, country2, payoutCurrency],
    queryFn: () =>
      AgentBalanceApi.getSnapshot({
        agentId,
        country: country2,
        currency: payoutCurrency,
      }).catch((err) => {
        if (err?.response?.status === 404) return null;
        throw err;
      }),
    enabled: snapshotReady,
    keepPreviousData: true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const snapshot = snapshotQuery.data ?? null;

  const required = Number(formData.receivedAmount || 0);
  const available = Number(snapshot?.available ?? snapshot?.availableBalance ?? 0);

  const noSnapshot = snapshotReady && snapshotQuery.isSuccess && snapshot === null;
  const insufficient = snapshotReady && !noSnapshot && required > 0 && available < required;
  const shortBy = Math.max(0, required - available);

  const amountInvalid =
    showErrors &&
    (formData.amount === "" ||
      isNaN(Number(formData.amount)) ||
      Number(formData.amount) <= 0);
  const receivedInvalid =
    showErrors &&
    (formData.receivedAmount === "" ||
      isNaN(Number(formData.receivedAmount)) ||
      Number(formData.receivedAmount) <= 0);
  const agentInvalid = showErrors && !formData.agentId;

  const selectedAgent = useMemo(
    () => agents?.find((a) => String(a.id) === String(formData.agentId)),
    [agents, formData.agentId]
  );
  const activeAgents = useMemo(
    () => (Array.isArray(agents) ? agents.filter((agent) => agent?.active !== false) : []),
    [agents]
  );
  const noActiveAgents = activeAgents.length === 0;

  const senderCountryLabel = useMemo(
    () => senderData?.countryName || toCountryName(senderData?.country) || "—",
    [senderData?.countryName, senderData?.country]
  );
  const recipientCountryLabel = useMemo(
    () => recipientData?.countryName || toCountryName(recipientData?.country) || "—",
    [recipientData?.countryName, recipientData?.country]
  );

  const handleTopUp = () => {
    if (!selectedAgent) return;
    const fallbackBackTo = `${location.pathname}${location.search || ""}`;
    navigate("/home/add-balance", {
      state: {
        backTo: backToUrl || fallbackBackTo,
        reason: noSnapshot ? "no-snapshot" : "insufficient",
        prefill: {
          agentId: String(selectedAgent.id),
          country: payoutCountry,
          currency: payoutCurrency,
          amount: noSnapshot ? "" : shortBy,
        },
      },
    });
  };

  const handleAddAgent = () => {
    const fallbackBackTo = `${location.pathname}${location.search || ""}`;
    navigate("/home/create-user", {
      state: {
        role: "AGENT",
        showBackButton: true,
        backTo: backToUrl || fallbackBackTo,
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);

    const { amount, receivedAmount, agentId } = formData;

    if (!amount || !receivedAmount || !agentId) {
      return toast.error("Please complete all fields.");
    }
    if (isNaN(Number(amount)) || Number(amount) <= 0) {
      return toast.error("Send amount must be a positive number.");
    }
    if (isNaN(Number(receivedAmount)) || Number(receivedAmount) <= 0) {
      return toast.error("Receive amount must be a positive number.");
    }
    if (noActiveAgents) {
      return toast.error(`No active payout agent is available for ${recipientCountryLabel}.`);
    }
    if (noSnapshot) {
      return toast.error("No balance snapshot exists for the selected agent and country.");
    }
    if (insufficient) {
      return toast.error(
        `Insufficient funds for selected agent. Available ${fmtMoney(
          available,
          payoutCurrency
        )}, requires ${fmtMoney(required, payoutCurrency)}.`
      );
    }
    if (!selectedAgent) return toast.error("Selected agent not found.");

    const fullTransfer = {
      sender: senderData,
      recipient: recipientData,
      transfer: {
        amount: Number(amount),
        receivedAmount: Number(receivedAmount),
        sendCurrency: senderCurrency,
        receiveCurrency: recipientCurrency,
        agentFirstName: selectedAgent.firstName,
        agentLastName: selectedAgent.lastName,
        agentId: selectedAgent.id,
      },
    };

    onSubmit(fullTransfer);
  };

  return (
    <section className="ui-panel ">
      <header className="ui-panel__header">
        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
          <h3 className="ui-title mb-0">Transfer Details</h3>
        </div>
      </header>

      <div className="col">
        <div>
          <div className={`rounded-3 ${amountInvalid ? "border border-danger" : "border-0"}`}>
            <CurrencyAmountInput
              label="Send amount"
              amount={formData.amount}
              onAmountChange={(val) => setFormData((p) => ({ ...p, amount: val }))}
              countryName={senderCountryLabel}
              currency={senderCurrency}
              onCurrencyChange={setSenderCurrency}
            />
          </div>
          {amountInvalid && <div className="text-danger small mt-1">Enter a valid positive amount.</div>}
        </div>

        <div className="mt-3">
          <div className={`rounded-3 ${receivedInvalid ? "border border-danger" : "border-0"}`}>
            <CurrencyAmountInput
              label="They will receive"
              amount={formData.receivedAmount}
              onAmountChange={(val) => setFormData((p) => ({ ...p, receivedAmount: val }))}
              countryName={recipientCountryLabel}
              currency={recipientCurrency}
              onCurrencyChange={setRecipientCurrency}
            />
          </div>
          {receivedInvalid && <div className="text-danger small mt-1">Enter a valid positive amount.</div>}
        </div>
      </div>

      <div className="mt-3">
        <label className="form-label fw-semibold">Agent</label>
        <ChooseAgentOption
          agents={agents}
          value={formData.agentId}
          onChange={(id) => setFormData((p) => ({ ...p, agentId: id }))}
          placeholder="-- Select Agent --"
          invalid={agentInvalid}
          autoScrollIntoView={false}
        />
        {agentInvalid && <div className="text-danger small mt-1">Please choose an agent.</div>}

        {noActiveAgents && (
          <div className="alert alert-warning py-2 px-3 mt-2 mb-0">
            <div className="small">
              <strong>No active payout agent is available</strong> for {recipientCountryLabel}.
            </div>
            <div className="small text-body-secondary mt-1">
              Add an agent for this payout country before creating the transfer.
            </div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={handleAddAgent}
              >
                Add agent
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={onBack}
              >
                Change recipient
              </button>
            </div>
          </div>
        )}

        {snapshotReady && snapshotQuery.isLoading && (
          <div className="small text-body-secondary mt-2">Checking agent balance…</div>
        )}

        {!snapshotReady && !noActiveAgents && (
          <div className="small text-body-secondary mt-2">
            Select agent & received amount to check balance eligibility.
          </div>
        )}

        {snapshotReady && snapshotQuery.isSuccess && snapshot === null && (
          <div className="alert alert-warning d-flex align-items-center gap-3 py-2 px-3 mt-2 mb-0">
            <span className="small">
              <strong>No balance snapshot found</strong> for this agent in {payoutCountryLabel} ({payoutCurrency}).
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary ms-auto"
              onClick={handleTopUp}
            >
              Add balance
            </button>
          </div>
        )}

        {snapshotReady && snapshotQuery.isSuccess && snapshot !== null && (
          <div className="mt-2">
            <div className="small text-body-secondary">
              Available: <strong>{fmtMoney(available, payoutCurrency)}</strong>
              {required > 0 && (
                <>
                  {" · "}Required: <strong>{fmtMoney(required, payoutCurrency)}</strong>
                </>
              )}
            </div>

            {insufficient && (
              <div className="alert alert-warning d-flex align-items-center gap-3 py-2 px-3 mt-2 mb-0">
                <span className="small">
                  <strong>Insufficient funds.</strong>{" "}
                  Short by <strong>{fmtMoney(shortBy, payoutCurrency)}</strong>.
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary ms-auto"
                  onClick={handleTopUp}
                >
                  Add balance
                </button>
              </div>
            )}
          </div>
        )}

        {snapshotReady && snapshotQuery.isError && (
          <div className="alert alert-danger py-2 px-3 mt-2 mb-0 small">
            {getErrorText(snapshotQuery.error)}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="ui-actions mt-4 w-100 justify-content-between">
        <button
          type="button"
          className="ui-btn"
          onClick={onBack}
        >
          <MdArrowBack /> Back
        </button>

        <button
          type="submit"
          className="btn btn-primary rounded-pill d-flex align-items-center gap-2 ms-auto"
          disabled={noActiveAgents || noSnapshot || insufficient}
          title={
            noActiveAgents
              ? `No active payout agent is available for ${recipientCountryLabel}`
              : noSnapshot
              ? "No balance snapshot exists; add balance first"
              : insufficient
              ? "Agent has insufficient balance"
              : "Submit"
          }
          onClick={handleSubmit}
        >
          Submit <MdChevronRight />
        </button>
      </div>
    </section>
  );
};

export default TransferDetailsForm;
